import React, { useState, useEffect } from 'react';
import { Users, Smartphone, CheckCircle, AlertCircle, UserPlus, BarChart3, Settings } from 'lucide-react';
import { api } from '../utils/api';

const AdminDashboard = ({ setCurrentPage, user }) => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    todayAttendance: 0,
    lateToday: 0,
    registeredPasskeys: 0 // เปลี่ยนจาก registeredFaces เป็น registeredPasskeys
  });
  const [loading, setLoading] = useState(true);

  // ตรวจสอบว่าเป็น Admin หรือไม่
  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    loadStats();
    
    // Auto refresh stats every 30 seconds
    const interval = setInterval(() => {
      loadStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const [employeesRaw, attendance] = await Promise.all([
        api.getEmployees(),
        api.getReports({
          type: 'daily',
          date_from: new Date().toISOString().split('T')[0],
          date_to: new Date().toISOString().split('T')[0]
        })
      ]);
  
      // 1. กรองบัญชี Admin สูงสุดออก
      const employees = employeesRaw.filter(emp => 
        emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ'
      );
  
      // 2. นับ Active Employees
      const activeCount = employees.filter(e => e.is_active == 1).length;

      // 3. นับพนักงานที่ลงทะเบียนอุปกรณ์ (Passkey) แล้ว
      const registeredCount = employees.filter(e => parseInt(e.has_passkey) > 0).length;

      // 4. นับการเข้างานวันนี้
      const attendanceCount = attendance.filter(a => 
        a.check_in &&               
        a.check_in !== '-' &&       
        a.check_in !== '00:00:00' && 
        a.employee_id !== 'ADMIN'
      ).length;

      // 5. นับสาย
      const lateCount = attendance.filter(a => 
        a.status &&                 
        a.status.includes('สาย') && 
        a.employee_id !== 'ADMIN'
      ).length;
  
      setStats({
        totalEmployees: employees.length,
        activeEmployees: activeCount,
        todayAttendance: attendanceCount,
        lateToday: lateCount,
        registeredPasskeys: registeredCount
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, description }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`${color} p-3 rounded-full text-white mr-4`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isAdmin ? 'แดชบอร์ดผู้ดูแลระบบ' : 'แดชบอร์ด'}
          </h1>
          <p className="text-gray-600 mt-2">ภาพรวมการทำงานของระบบและข้อมูลสำคัญ</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard 
          title="บุคลากรทั้งหมด" 
          value={stats.totalEmployees} 
          icon={Users} 
          color="bg-blue-500"
          description="รวมทั้งที่ใช้งานและไม่ใช้งาน"
        />
        <StatCard 
          title="บุคลากรที่ใช้งาน" 
          value={stats.activeEmployees} 
          icon={Users} 
          color="bg-green-500"
          description="สามารถลงเวลาได้"
        />
        <StatCard 
          title="ลงทะเบียนอุปกรณ์" 
          value={stats.registeredPasskeys} 
          icon={Smartphone} 
          color="bg-purple-500"
          description="พร้อมใช้งาน (Passkey)"
        />
        <StatCard 
          title="เข้างานวันนี้" 
          value={stats.todayAttendance} 
          icon={CheckCircle} 
          color="bg-indigo-500"
          description="ลงเวลาเข้าแล้ว"
        />
        <StatCard 
          title="มาสายวันนี้" 
          value={stats.lateToday} 
          icon={AlertCircle} 
          color="bg-red-500"
          description="เข้างานหลังเวลาที่กำหนด"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">ภาพรวมระบบ</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">อัตราลงทะเบียนอุปกรณ์:</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium">
                  {stats.totalEmployees > 0 ? Math.round((stats.registeredPasskeys / stats.totalEmployees) * 100) : 0}%
                </span>
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full" 
                    style={{ 
                      width: `${stats.totalEmployees > 0 ? (stats.registeredPasskeys / stats.totalEmployees) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">อัตราเข้างานวันนี้:</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium">
                  {stats.activeEmployees > 0 ? Math.round((stats.todayAttendance / stats.activeEmployees) * 100) : 0}%
                </span>
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-500 h-2 rounded-full" 
                    style={{ 
                      width: `${stats.activeEmployees > 0 ? (stats.todayAttendance / stats.activeEmployees) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">อัตรามาสาย:</span>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-red-600">
                  {stats.todayAttendance > 0 ? Math.round((stats.lateToday / stats.todayAttendance) * 100) : 0}%
                </span>
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-500 h-2 rounded-full" 
                    style={{ 
                      width: `${stats.todayAttendance > 0 ? (stats.lateToday / stats.todayAttendance) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">การดำเนินการด่วน</h2>
          <div className="space-y-3">
            <button 
              onClick={() => setCurrentPage('employees')}
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 flex items-center justify-center space-x-2 transition-colors"
            >
              <UserPlus className="h-5 w-5" />
              <span>จัดการบุคลากร</span>
            </button>
            <button 
              onClick={() => setCurrentPage('daily-report')}
              className="w-full bg-green-500 text-white py-3 px-4 rounded-md hover:bg-green-600 flex items-center justify-center space-x-2 transition-colors"
            >
              <BarChart3 className="h-5 w-5" />
              <span>ดูรายงานรายวัน</span>
            </button>
            {isAdmin && (
              <button 
                onClick={() => setCurrentPage('settings')}
                className="w-full bg-gray-500 text-white py-3 px-4 rounded-md hover:bg-gray-600 flex items-center justify-center space-x-2 transition-colors"
              >
                <Settings className="h-5 w-5" />
                <span>ตั้งค่าระบบ</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">สรุปข้อมูลด่วน</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">{stats.totalEmployees}</div>
            <div className="text-sm text-gray-600">บุคลากรทั้งหมด</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.activeEmployees}</div>
            <div className="text-sm text-gray-600">ใช้งานอยู่</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">{stats.registeredPasskeys}</div>
            <div className="text-sm text-gray-600">ลงทะเบียนอุปกรณ์</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-indigo-600">{stats.todayAttendance}</div>
            <div className="text-sm text-gray-600">เข้างานวันนี้</div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-yellow-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-yellow-900 mb-3">คำแนะนำ</h3>
        <ul className="space-y-2 text-yellow-800">
          {stats.registeredPasskeys < stats.activeEmployees && (
            <li className="flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>มีบุคลากร {stats.activeEmployees - stats.registeredPasskeys} คนที่ยังไม่ได้ลงทะเบียนอุปกรณ์ กรุณาดำเนินการให้ครบถ้วน</span>
            </li>
          )}
          {stats.lateToday > 0 && (
            <li className="flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>มีบุคลากรมาสาย {stats.lateToday} คนในวันนี้ ควรติดตามและแจ้งเตือน</span>
            </li>
          )}
          {stats.todayAttendance < stats.activeEmployees * 0.8 && (
            <li className="flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>อัตราการเข้างานวันนี้ต่ำกว่า 80% ควรตรวจสอบสาเหตุ</span>
            </li>
          )}
          {stats.totalEmployees === 0 && (
            <li className="flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>ยังไม่มีบุคลากรในระบบ เริ่มต้นด้วยการเพิ่มบุคลากรคนแรก</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default AdminDashboard;